import React, { useState, useRef, useEffect } from 'react';
import type { BadgeType } from './BadgeIcon';
import { useTranslation } from 'react-i18next';

interface BadgeTooltipProps {
  badgeType: BadgeType;
  children: React.ReactNode;
}

const BADGE_INFO: Record<BadgeType, { nameKey: string; descriptionKey: string }> = {
  founding_creator: {
    nameKey: 'badges.foundingCreator.name',
    descriptionKey: 'badges.foundingCreator.description',
  },
  verified_identity: {
    nameKey: 'badges.verifiedIdentity.name',
    descriptionKey: 'badges.verifiedIdentity.description',
  },
  verified_expert: {
    nameKey: 'badges.verifiedExpert.name',
    descriptionKey: 'badges.verifiedExpert.description',
  },
  verified_journalist: {
    nameKey: 'badges.verifiedJournalist.name',
    descriptionKey: 'badges.verifiedJournalist.description',
  },
  official: {
    nameKey: 'badges.official.name',
    descriptionKey: 'badges.official.description',
  },
};

export function BadgeTooltip({ badgeType, children }: BadgeTooltipProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('top');
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const info = BADGE_INFO[badgeType];

  useEffect(() => {
    if (!info) return;
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current.offsetHeight;

      // Check if tooltip would go above viewport
      if (triggerRect.top - tooltipHeight - 8 < 0) {
        setPosition('bottom');
      } else {
        setPosition('top');
      }
    }
  }, [isVisible, info]);

  if (!info) return <>{children}</>;

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className={`
            absolute z-50 px-3 py-2 text-xs
            bg-[#16161e] border border-white/10 rounded-lg shadow-lg
            whitespace-nowrap
            transition-opacity duration-150
            ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
            left-1/2 -translate-x-1/2
          `}
        >
          <div className="font-semibold text-white">{t(info.nameKey)}</div>
          <div className="text-[#a0a0b0] mt-0.5">{t(info.descriptionKey)}</div>
          {/* Tooltip arrow */}
          <div
            className={`
              absolute left-1/2 -translate-x-1/2 w-2 h-2
              bg-[#16161e] border-white/10 rotate-45
              ${position === 'top'
                ? 'bottom-0 translate-y-1/2 border-r border-b'
                : 'top-0 -translate-y-1/2 border-l border-t'
              }
            `}
          />
        </div>
      )}
    </div>
  );
}

export default BadgeTooltip;
